import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Sparkles } from "lucide-react";
import { useAuth } from "../../lib/AuthContext";
import { useLang } from "../../lib/LangContext";

const links = [
  { to: "/", key: "Home" },
  { to: "/how-it-works", key: "How It Works" },
  { to: "/services", key: "Services" },
  { to: "/examples", key: "Examples" },
  { to: "/pricing", key: "Pricing" },
  { to: "/for-businesses", key: "For Businesses" },
  { to: "/faq", key: "FAQ" },
];

export const Logo = ({ light = false }) => (
  <span className="inline-flex items-center gap-2 font-display text-xl font-bold tracking-tight">
    <span className="inline-flex h-8 w-8 items-center justify-center rounded-xl bg-[#F95738] text-white">
      <Sparkles className="h-4 w-4" aria-hidden="true" />
    </span>
    <span className={light ? "text-white" : "text-slate-900"}>PostMint</span>
  </span>
);

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user, logout } = useAuth();
  const { lang, setLang, tr } = useLang();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const dashboardPath = user && user.role === "admin" ? "/admin" : "/dashboard";

  return (
    <header
      data-testid="site-navbar"
      className={`sticky top-0 z-50 w-full border-b transition-colors duration-300 ${
        scrolled ? "border-slate-200 bg-white/85 backdrop-blur-md" : "border-transparent bg-[#FAFAFA]"
      }`}
    >
      <nav className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-5 py-4 lg:px-8">
        <Link to="/" data-testid="nav-logo-link" aria-label="PostMint home">
          <Logo />
        </Link>

        <ul className="hidden items-center gap-1 lg:flex">
          {links.map((l) => (
            <li key={l.to}>
              <NavLink
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-link-${l.to === "/" ? "home" : l.to.slice(1)}`}
                className={({ isActive }) =>
                  `rounded-full px-3 py-2 text-sm font-medium transition-colors ${
                    isActive ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                  }`
                }
              >
                {tr(l.key)}
              </NavLink>
            </li>
          ))}
        </ul>

        <div className="hidden items-center gap-3 lg:flex">
          <div className="flex items-center rounded-full border border-slate-200 p-0.5" role="group" aria-label="Language">
            {["en", "nl"].map((code) => (
              <button
                key={code}
                type="button"
                data-testid={`lang-switch-${code}`}
                onClick={() => setLang(code)}
                className={`rounded-full px-2.5 py-1 text-xs font-semibold uppercase transition-colors ${
                  lang === code ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-900"
                }`}
              >
                {code}
              </button>
            ))}
          </div>

          {user ? (
            <>
              <Link
                to={dashboardPath}
                data-testid="nav-dashboard-link"
                className="text-sm font-medium text-slate-700 hover:text-slate-900"
              >
                {tr("Dashboard")}
              </Link>
              <button
                type="button"
                onClick={logout}
                data-testid="nav-logout-button"
                className="rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-100"
              >
                {tr("Logout")}
              </button>
            </>
          ) : (
            <>
              <Link
                to="/login"
                data-testid="nav-login-link"
                className="text-sm font-medium text-slate-700 hover:text-slate-900"
              >
                {tr("Login")}
              </Link>
              <Link
                to="/get-started"
                data-testid="nav-get-started-button"
                className="rounded-full bg-[#F95738] px-5 py-2.5 text-sm font-semibold text-white shadow-[0_6px_20px_rgba(249,87,56,0.35)] transition-transform duration-200 hover:-translate-y-0.5 hover:bg-[#E0482B]"
              >
                {tr("Get Started")}
              </Link>
            </>
          )}
        </div>

        <button
          type="button"
          data-testid="mobile-menu-toggle"
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-slate-200 text-slate-700 lg:hidden"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </nav>

      {open && (
        <div data-testid="mobile-menu-panel" className="border-t border-slate-200 bg-white px-5 pb-6 pt-2 lg:hidden">
          <ul className="flex flex-col">
            {links.map((l) => (
              <li key={l.to}>
                <NavLink
                  to={l.to}
                  end={l.to === "/"}
                  data-testid={`mobile-nav-link-${l.to === "/" ? "home" : l.to.slice(1)}`}
                  className={({ isActive }) =>
                    `block rounded-xl px-3 py-3 text-base font-medium ${
                      isActive ? "bg-slate-100 text-slate-900" : "text-slate-700"
                    }`
                  }
                >
                  {tr(l.key)}
                </NavLink>
              </li>
            ))}
          </ul>
          <div className="mt-4 flex flex-col gap-3">
            {user ? (
              <>
                <Link
                  to={dashboardPath}
                  data-testid="mobile-nav-dashboard"
                  className="rounded-full border border-slate-200 px-4 py-3 text-center text-base font-semibold text-slate-800"
                >
                  {tr("Dashboard")}
                </Link>
                <button
                  type="button"
                  onClick={logout}
                  data-testid="mobile-nav-logout"
                  className="rounded-full bg-slate-900 px-4 py-3 text-base font-semibold text-white"
                >
                  {tr("Logout")}
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  data-testid="mobile-nav-login"
                  className="rounded-full border border-slate-200 px-4 py-3 text-center text-base font-semibold text-slate-800"
                >
                  {tr("Login")}
                </Link>
                <Link
                  to="/get-started"
                  data-testid="mobile-nav-get-started"
                  className="rounded-full bg-[#F95738] px-4 py-3 text-center text-base font-semibold text-white"
                >
                  {tr("Get Started")}
                </Link>
              </>
            )}
            <div className="flex items-center gap-2 pt-1">
              {["en", "nl"].map((code) => (
                <button
                  key={code}
                  type="button"
                  data-testid={`mobile-lang-switch-${code}`}
                  onClick={() => setLang(code)}
                  className={`rounded-full border px-4 py-2 text-sm font-semibold uppercase ${
                    lang === code ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-600"
                  }`}
                >
                  {code}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
